import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Inject,
  forwardRef
} from "@angular/core";


//Models
import { AssessmentFormat } from "app/core/models/assessment-format";
import { AssessmentOption } from "app/core/models/assessment-option";
import { AssessmentSelectedMethod } from "app/core/models/assessment-selected-method";
import { AssessmentTimingOption } from "app/core/models/assessment-timing-option";
import { AssessmentLevelOption } from "app/core/models/assessment-level-option";
import { CustomError } from "app/core/models/custom-error";
import { Dialog } from "app/core/models/dialog";
import { Loading } from "app/core/models/loading";
import { UserCourseComponent } from "../user-course/user-course.component";
import { CurriculumReview } from "app/core/models/curriculum-review";
import { AssessmentMethodOption } from "app/core/models/assessment-method-option";
import { finalize } from "rxjs/operators";
import { callbackify } from "util";
import { AssessmentTimingOptionSelected } from "app/core/models/assessment-timing-option-selected";
import { AssessmentLevelOptionSelected } from "app/core/models/assessment-level-option-selected";


//Services
import { DimmerService } from "app/core/services/dimmer.service";
import { AssessmentService } from "app/core/services/assessment.service";
import { CourseService } from "app/core/services/course.service";
import { AlertService } from "app/core/services/alert.service";


//Libraries
import { throwError } from "rxjs";

@Component({
  selector: "app-user-step-assessment",
  templateUrl: "./user-step-assessment.component.html",
  styleUrls: ["./user-step-assessment.component.scss"]
})
export class UserStepAssessmentComponent implements OnInit {
  @Input()
  courseId: number;
  @Input()
  review: CurriculumReview;

  // @Output()
  // nextStep: EventEmitter<any> = new EventEmitter();

  curriculumReview: CurriculumReview;

  assessmentFormat: AssessmentFormat;

  //for form
  assessmentMethodOptions: Array<AssessmentOption> = [];
  assessmentLevelOptions: Array<AssessmentLevelOption> = [];
  assessmentTimingOptions: Array<AssessmentTimingOption> = [];

  //already submitted data
  selectedAssessmentMethods: Array<AssessmentSelectedMethod> = [];

  //flags
  shouldShowForm: boolean = false;

  optionsLoaded: boolean = false;
  levelOptionsLoaded: boolean = false;
  timingOptionsLoaded: boolean = false;
  selectedLevelOptionsLoaded: boolean = false;
  selectedTimingOptionsLoaded: boolean = false;

  isAssessmentMethodSelected: boolean = true;
  isWeightNumerical: boolean = true;
  isLevelSelected: boolean = true;
  isTimingSelected: boolean = true;

  dialogUpdating: boolean = false;

  formProcessing: boolean = false;

  // assessmentMethodValues: Array<AssessmentMethodValue> = [];
  totalWeight: number;

  //form values
  selectedAssessmentMethod: AssessmentSelectedMethod;
  timingOptionsForForm: Array<AssessmentTimingOption> = [];
  levelsForForm: Array<AssessmentLevelOption> = [];

  // assessmentLevelOptionsForForm: Array<AssessmentLevelOption> = [];

  //without values

  //loading flag
  loading: Loading;

  //show saving status
  shouldShowSavingStatus: boolean = false;
  savingStatusTitle: string;
  savingStatusText: string;
  savingStatus: string;

  //error
  showError: boolean = false;
  errorMessage: string = "";

  //action type
  action: string;

  //delete pending
  deletePendingAssessment: AssessmentSelectedMethod;

  //errors
  loadError: CustomError;

  actionErrorTitle: string = "Error";
  actionErrors: Array<CustomError> = [];

  dialog: Dialog;

  constructor(
    @Inject(forwardRef(() => UserCourseComponent))
    private _parent: UserCourseComponent,
    private assessmentService: AssessmentService,
    private dimmerService: DimmerService,
    private courseService: CourseService,
    private alertService: AlertService
  ) {}

  /********************************************
   * Loads assessment methods for this course
   * Errors: Load errors
   * V.1
   * @param callback
   *********************************************/
  async loadAssessmentMethods() {
    try {
      //get assessment format first
      let assessmentFormat = await this.assessmentService.getAssessmentFormatByCourseId(
        this.courseId
      );

      this.assessmentFormat = assessmentFormat;

      if (!this.assessmentFormat.isEnabled) {
        // assessment method is not enabled
        return;
      } else {
        //assessment is enabled

        if (this.assessmentFormat.format == "text") {
          this.selectedAssessmentMethods = await this.assessmentService.getAssessmentMethodsTextForCourse(
            this.courseId,
            assessmentFormat.id
          );
        } else if (this.assessmentFormat.format == "list") {
          //get available method options for form
          this.assessmentMethodOptions = await this.assessmentService.getAssessmentMethodOptionsByCourseId(
            this.courseId,
            assessmentFormat.id
          );
          // this.assessmentMethodOptions = assessmentMethodOptions;

          //let currently selected assessment methods
          let selectedAssessmentMethods = await this.assessmentService.getSelectedAssessmentMethods(
            this.courseId,
            this.assessmentFormat.id
          );
          this.selectedAssessmentMethods = selectedAssessmentMethods;
        }

        //assessment weight
        if (assessmentFormat.isWeightRequired) {
          let totalWeight: number = 0;
          this.selectedAssessmentMethods.forEach(selectedAssessmentMethod => {
            totalWeight = totalWeight + selectedAssessmentMethod.weight;
            this.totalWeight = totalWeight;
          });
        }

        ///If timing is required, get all options first and then get selected options
        if (assessmentFormat.isTimingRequired) {
          if (assessmentFormat.timingFormat == "checkbox") {
            //get timing options
            this.assessmentTimingOptions = await this.assessmentService.getAssessmentTimingOptionsByCourseId(
              this.courseId,
              assessmentFormat.id
            );

            await this.loadSelectedTimingValues(error => {
              if (error) {
                throw error;
              }
            });
          }
        }

        //if level is required, get all options first and get selected levels
        if (assessmentFormat.isLevelRequired) {
          if (assessmentFormat.levelFormat == "checkbox") {
            this.assessmentLevelOptions = await this.assessmentService.getAssessmentLevelOptionsByCourseId(
              this.courseId,
              assessmentFormat.id
            );
          }

          await this.loadSelectedLevelValues(error => {
            if (error) {
              throw error;
            }
          });
        }

        return;
      }
    } catch (error) {
      throw error;
    }
  }

  /**********************************************
   * Loads assessment methods
   * V.1
   *********************************************/
  async ngOnInit() {
    this.loading = new Loading(true, "Loading..");

    // console.log("Loading");

    try{
      await this.loadAssessmentMethods();
    }
    catch(error){
      this.loadError = error;
    }
    finally{
      this.loading.isLoading = false;
    }

    // this.loadAssessmentMethods(error => {
    //   if (error) {
    //     //load error
    //     this.loadError = error;
    //   }

    //   this.loading.isLoading = false;
    // });
  }

  /**********************************************
   * Load selected timeing values for each assessment method
   * Error: Load errors
   * V.1
   ******************************************** */
  async loadSelectedTimingValues(callback) {
    let numCheckedMethod = 0;
    let errors: Array<CustomError> = [];

    if (this.selectedAssessmentMethods.length == 0) {
      //console.log("calling back..");
      // callback(errors);
    }

    if (this.assessmentFormat.timingFormat == "checkbox") {
      try {
        for (let selectedMethod of this.selectedAssessmentMethods) {
          //initially assign all timing options as "not selected"
          this.assessmentTimingOptions.forEach(timing => {
            selectedMethod.timings.push(
              new AssessmentTimingOption(
                timing.id,
                timing.description,
                timing.isOtherTextRequired,
                timing.flag,
                timing.otherText,
                false
              )
            );
          });

          //get timings if required
          let selectedTimings: Array<
            AssessmentTimingOptionSelected
          > = await this.assessmentService.getSelectedAssessmentTimings(
            this.courseId,
            this.assessmentFormat.id,
            selectedMethod.id
          );

          let selectedTimingArray: Array<AssessmentTimingOptionSelected> = [];

          //conver to index array format
          selectedTimings.forEach(selectedTiming => {
            selectedTimingArray[selectedTiming.timingOptionId] = selectedTiming;
          });

          selectedMethod.timings.forEach(timingOption => {
            if (selectedTimingArray[timingOption.id]) {
              timingOption.isSelected = true;
              timingOption.otherText =
                selectedTimingArray[timingOption.id].otherText;
            } else {
              timingOption.isSelected = false;
            }
          });
        }

        return callback();
      } catch (error) {
        return callback(error);
      }
    }
  }

  /**********************************************
   * Load selected levels for each assessment
   * Callback Load Errors
   * V.1
   ******************************************** */
  async loadSelectedLevelValues(callback) {
    let numCheckedMethod = 0;
    let errors: Array<CustomError> = [];

    if (this.selectedAssessmentMethods.length == 0) {
      // callback(errors);
    }

    try {
      if (this.assessmentFormat.levelFormat == "checkbox") {
        for (let selectedMethod of this.selectedAssessmentMethods) {
          //initially assign all timing options as "not selected"
          this.assessmentLevelOptions.forEach(level => {
            selectedMethod.levels.push(
              new AssessmentLevelOption(
                level.id,
                level.description,
                level.isOtherTextRequired,
                level.flag,
                level.otherText,
                false
              )
            );
          });

          //get selected levels if required
          let selectedLevels: Array<
            AssessmentLevelOptionSelected
          > = await this.assessmentService.getSelectedAssessmentLevels(
            this.courseId,
            this.assessmentFormat.id,
            selectedMethod.id
          );

          let selectedLevelArray: Array<AssessmentLevelOptionSelected> = [];

          selectedLevels.forEach(selectedLevel => {
            selectedLevelArray[selectedLevel.levelOptionId] = selectedLevel;
          });

          selectedMethod.levels.forEach(levelOption => {
            if (selectedLevelArray[levelOption.id]) {
              levelOption.isSelected = true;
              levelOption.otherText =
                selectedLevelArray[levelOption.id].otherText;
            } else {
              levelOption.isSelected = false;
            }
          });
        }
      }

      return callback();
    } catch (error) {
      return callback(error);
    }
  }

  /**********************************************
   * Clear All variables
   * V.1
   ******************************************** */
  loadCompleted() {
    //console.log("load completed");
    this.clearAll();
  }

  /*********************************************
   * Add assessment method button was clicked
   * - Initialize new assessment method form
   * V.1
   ********************************************/
  showAddAssessmentForm() {
    this.actionErrors = [];

    this.selectedAssessmentMethod = new AssessmentSelectedMethod();

    //if level is required,load predefined level options
    if (this.assessmentFormat.isLevelRequired) {
      this.levelsForForm = [];

      this.assessmentLevelOptions.forEach(levelOption => {
        this.levelsForForm.push(
          new AssessmentLevelOption(
            levelOption.id,
            levelOption.description,
            levelOption.isOtherTextRequired,
            levelOption.flag,
            null,
            false
          )
        );
      });
    }

    //if timing is reuired, load predefined timing options
    if (this.assessmentFormat.isTimingRequired) {
      this.timingOptionsForForm = [];

      this.assessmentTimingOptions.forEach(timingOption => {
        this.timingOptionsForForm.push(
          new AssessmentTimingOption(
            timingOption.id,
            timingOption.description,
            timingOption.isOtherTextRequired,
            timingOption.flag,
            null,
            false
          )
        );
      });
    }

    //show
    this.dimmerService.dimWindow(true);
    this.shouldShowForm = true;
    // this.shouldShowSavingStatus = false;
  }

  /*********************************************
   * Verify Input
   * V.1
   ********************************************/

  verifyNumerical() {
    //console.log(isNaN(this.selectedAssessmentMethod.weight));

    if (isNaN(this.selectedAssessmentMethod.weight)) {
      this.isWeightNumerical = false;
    } else if (this.selectedAssessmentMethod.weight > 100) {
      this.isWeightNumerical = false;
    } else {
      this.isWeightNumerical = true;
    }
  }

  /**********************************************
   * Process Edit Assessment Method
   * Callback Load Errors
   * V.1
   *********************************************/
  async processEditAssessmentMethod(callback) {
    //console.log(this.selectedAssessmentMethod);

    try {
      //id is set == UPDATE current assessment method
      let assessmentSelectedMethod: AssessmentSelectedMethod;

      if (this.assessmentFormat.format == "text") {
        assessmentSelectedMethod = await this.assessmentService.updateSelectedAssessmentMethodText(
          this.courseId,
          this.assessmentFormat.id,
          this.selectedAssessmentMethod
        );
      } else if (this.assessmentFormat.format == "list") {
        assessmentSelectedMethod = await this.assessmentService.updateSelectedAssessmentMethodOption(
          this.courseId,
          this.assessmentFormat.id,
          this.selectedAssessmentMethod
        );
      }

      if (assessmentSelectedMethod) {
        //replace with the updated method
        for (let key of Object.keys(this.selectedAssessmentMethods)) {
          if (
            this.selectedAssessmentMethods[key].id ==
            assessmentSelectedMethod.id
          ) {
            this.selectedAssessmentMethods[key] = assessmentSelectedMethod;
            break;
          }
        }

        if (
          this.assessmentFormat.isTimingRequired &&
          this.assessmentFormat.timingFormat == "checkbox"
        ) {
          let selectedTimingOptions = [];

          // first delete all selections
          await this.assessmentService.deleteSelectedAssessmentTimingValues(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id
          );

          //only submit selected timing options
          this.timingOptionsForForm.forEach(timingOption => {
            if (timingOption.isSelected) {
              selectedTimingOptions.push(timingOption);
            }
          });

          await this.assessmentService.addSelectedAssessmentTimingValues(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id,
            selectedTimingOptions
          );
          assessmentSelectedMethod.timings = this.timingOptionsForForm;
        }

        if (
          this.assessmentFormat.isLevelRequired &&
          this.assessmentFormat.levelFormat == "checkbox"
        ) {
          let selectedLevelOptions = [];
          // first delete all selections
          await this.assessmentService.deleteSelectedAssessmentLevelValues(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id
          );

          //then re-add selected options
          this.levelsForForm.forEach(levelOption => {
            if (levelOption.isSelected) {
              selectedLevelOptions.push(levelOption);
            }
          });

          await this.assessmentService.addSelectedAssessmentLevelOptions(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id,
            selectedLevelOptions
          );

          assessmentSelectedMethod.levels = this.levelsForForm;
        }

        if (this.assessmentFormat.isWeightRequired) {
          this.updateTotalWeight();
        }
      }

      return callback();
    } catch (error) {
      return callback(error);
    }
  }

  /************************************
   * Submit new assessment method
   * Callback Load errors
   * V.1
   ***********************************/
  async processAddAssessmentMethod(callback) {
    //console.log(this.selectedAssessmentMethod);

    try {
      let assessmentSelectedMethod: AssessmentSelectedMethod;

      if (this.assessmentFormat.format == "list") {
        assessmentSelectedMethod = await this.assessmentService.addSelectedAssessmentMethodOption(
          this.courseId,
          this.assessmentFormat.id,
          this.selectedAssessmentMethod
        );
      } else if (this.assessmentFormat.format == "text") {
        assessmentSelectedMethod = await this.assessmentService.addAssessmentMethodText(
          this.courseId,
          this.assessmentFormat.id,
          this.selectedAssessmentMethod
        );
      }

      if (assessmentSelectedMethod) {
        if (
          this.assessmentFormat.isTimingRequired &&
          this.assessmentFormat.timingFormat == "checkbox"
        ) {
          let selectedTimingOptions = [];

          this.timingOptionsForForm.forEach(timingOption => {
            if (timingOption.isSelected) {
              selectedTimingOptions.push(timingOption);
            }
          });

          this.assessmentService.addSelectedAssessmentTimingValues(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id,
            selectedTimingOptions
          );

          assessmentSelectedMethod.timings = this.timingOptionsForForm;
        }

        if (
          this.assessmentFormat.isLevelRequired &&
          this.assessmentFormat.levelFormat == "checkbox"
        ) {
          let selectedLevelOptions = [];

          this.levelsForForm.forEach(levelOption => {
            if (levelOption.isSelected) {
              selectedLevelOptions.push(levelOption);
            }
          });

          await this.assessmentService.addSelectedAssessmentLevelOptions(
            this.courseId,
            this.assessmentFormat.id,
            assessmentSelectedMethod.id,
            selectedLevelOptions
          );

          assessmentSelectedMethod.levels = this.levelsForForm;
        }

        this.selectedAssessmentMethods.push(assessmentSelectedMethod);

        if (this.assessmentFormat.isWeightRequired) {
          this.updateTotalWeight();
        }
      }

      return callback();
    } catch (error) {
      console.log(error);
      return callback(error);
    }
  }

  verifyLevelSelected() {
    for (let level of this.levelsForForm) {
      if (level.isSelected) {
        return true;
      }
    }
    return false;
  }

  verifyTimingSelected() {
    for (let timing of this.timingOptionsForForm) {
      if (timing.isSelected) {
        return true;
      }
    }
    return false;
  }

  /************************************
   * Submit new assessment method
   * Action Errors
   * V.1
   ***********************************/
  async submitForm() {
    this.actionErrors = [];
    this.formProcessing = true;

    //is method selected?
    if (
      this.assessmentFormat.format == "list" &&
      !this.selectedAssessmentMethod.methodId
    ) {
      this.isAssessmentMethodSelected = false;
    } else if (
      this.assessmentFormat.format == "text" &&
      !this.selectedAssessmentMethod.methodName
    ) {
      this.isAssessmentMethodSelected = false;
    } else {
      this.isAssessmentMethodSelected = true;
    }

    //isweight numeric and < 100
    if (this.assessmentFormat.isWeightRequired) {
      if (
        !this.selectedAssessmentMethod.weight ||
        isNaN(this.selectedAssessmentMethod.weight)
      ) {
        this.isWeightNumerical = false;
      } else if (this.selectedAssessmentMethod.weight > 100) {
        this.isWeightNumerical = false;
      } else {
        this.isWeightNumerical = true;
      }
    } else {
      this.isWeightNumerical = true;
    }

    //is timing selected
    if (this.assessmentFormat.isTimingRequired) {
      if (this.assessmentFormat.timingFormat == "checkbox") {
        if (!this.verifyTimingSelected()) {
          this.isTimingSelected = false;
        } else {
          this.isTimingSelected = true;
        }
      } else if (this.assessmentFormat.timingFormat == "text") {
        if (!this.selectedAssessmentMethod.timingText) {
          this.isTimingSelected = false;
        } else {
          this.isTimingSelected = true;
        }
      }
    }

    //is level selected
    if (this.assessmentFormat.isLevelRequired) {
      if (this.assessmentFormat.levelFormat == "checkbox") {
        if (!this.verifyLevelSelected()) {
          this.isLevelSelected = false;
        } else {
          this.isLevelSelected = true;
        }
      } else if (this.assessmentFormat.levelFormat == "text") {
        if (!this.selectedAssessmentMethod.levelText) {
          this.isLevelSelected = false;
        } else {
          this.isLevelSelected = true;
        }
      }
    }

    //proceed
    if (
      !this.isAssessmentMethodSelected ||
      !this.isWeightNumerical ||
      !this.isTimingSelected ||
      !this.isLevelSelected
    ) {
      this.formProcessing = false;
    } else {
      let methodCompleted: boolean = false;
      let levelsCompleted: boolean = this.assessmentFormat.isLevelRequired
        ? false
        : true;
      let timingCompleted: boolean = this.assessmentFormat.isTimingRequired
        ? false
        : true;

      //if id exists, it's editing
      if (this.selectedAssessmentMethod.id) {
        //console.log("editing");

        this.processEditAssessmentMethod(error => {
          if (error) {
            console.log(error);
            this.actionErrors.push(error);
          }

          this.loading.isLoading = false;
          this.dimmerService.dimWindow(false);
          this.shouldShowForm = false;
          this.formProcessing = false;
        });
      } else {
        this.processAddAssessmentMethod(error => {
          if (error) {
            console.log(error);
            this.actionErrors.push(error);
          }

          this.loading.isLoading = false;
          this.dimmerService.dimWindow(false);
          this.shouldShowForm = false;
          this.formProcessing = false;
        });
      }
    }
  }

  updateTotalWeight() {
    // console.log("Updating weight..");

    this.totalWeight = 0;
    this.selectedAssessmentMethods.forEach(assessmentMethod => {
      this.totalWeight += +assessmentMethod.weight;
    });
  }

  /*********************************************
   * Initialize Show Edit Assessment Form
   * V.1
   ********************************************/
  showEditAssessmentForm(assessment: AssessmentSelectedMethod) {
    //console.log(assessment);

    this.selectedAssessmentMethod = new AssessmentSelectedMethod(
      assessment.id,
      assessment.methodId,
      assessment.methodName,
      assessment.description,
      assessment.weight,
      this.assessmentFormat.isTimingRequired &&
      this.assessmentFormat.timingFormat == "text"
        ? assessment.timingText
        : null,
      this.assessmentFormat.isLevelRequired &&
      this.assessmentFormat.levelFormat == "text"
        ? assessment.levelText
        : null
    );

    if (
      this.assessmentFormat.isLevelRequired &&
      this.assessmentFormat.levelFormat == "checkbox"
    ) {
      this.levelsForForm = [];

      assessment.levels.forEach(levelOption => {
        this.levelsForForm.push(
          new AssessmentLevelOption(
            levelOption.id,
            levelOption.description,
            levelOption.isOtherTextRequired,
            levelOption.flag,
            levelOption.otherText,
            levelOption.isSelected
          )
        );
      });
    }

    //if timing is reuired, load predefined timing options
    if (
      this.assessmentFormat.isTimingRequired &&
      this.assessmentFormat.timingFormat == "checkbox"
    ) {
      this.timingOptionsForForm = [];

      assessment.timings.forEach(timingOption => {
        this.timingOptionsForForm.push(
          new AssessmentTimingOption(
            timingOption.id,
            timingOption.description,
            timingOption.isOtherTextRequired,
            timingOption.flag,
            timingOption.otherText,
            timingOption.isSelected
          )
        );
      });
    }

    this.dimmerService.dimWindow(true);
    this.shouldShowForm = true;
  }

  /***********************************
   * Delete assessment was initiated
   * V.1
   ***********************************/
  deleteAssessment(assessment: AssessmentSelectedMethod) {
    //copy the object so proceedDelete function has access to it.
    this.deletePendingAssessment = assessment;

    this.dimmerService.dimWindow(true);
    this.dialog = new Dialog(
      true,
      "Please confirm",
      'Are you sure you want to delete this assessment method "' +
        assessment.description +
        '" ?',
      "No, don't delete",
      "Yes, delete the assessment method",
      "Processing.."
    );
  }

  /************************************
   * Cancel delete - close saving status
   * V.1
   ***********************************/
  closeDialog() {
    this.clearAll();
  }

  /************************************
   * User confirmed delete
   * V.1
   * Action Errors
   ***********************************/
  async proceedDeleteMethod() {
    this.dialogUpdating = true;

    //delete selected assessment method

    try {
      if (this.assessmentFormat.format == "list") {
        await this.assessmentService.deleteAssessmentMethodValue(
          this.courseId,
          this.assessmentFormat.id,
          this.deletePendingAssessment
        );
      } else if (this.assessmentFormat.format == "text") {
        await this.assessmentService.deleteAssessmentMethodTextValue(
          this.courseId,
          this.assessmentFormat.id,
          this.deletePendingAssessment
        );
      }

      for (let key of Object.keys(this.selectedAssessmentMethods)) {
        if (
          this.selectedAssessmentMethods[+key].id ==
          this.deletePendingAssessment.id
        ) {
          this.selectedAssessmentMethods.splice(+key, 1);
          break;
        }
      }
    } catch (error) {
      this.actionErrors.push(error);
    } finally {
      this.dialogUpdating = false;
      if (this.dialog) {
        this.dialog.show = false;
      }
      this.dimmerService.dimWindow(false);
    }
  }

  /************************************
   * Close Form
   * V.1
   ***********************************/
  closeForm() {
    this.clearAll();
  }

  // /**
  //  * Check if this step is completed. If there is at least one assessment method, consider completed.
  //  */
  // isStepCompleted() {
  //   // for (let value of this.selectedAssessmentMethods) {
  //   //   if (value.selectedId) {
  //   //     return true;
  //   //   }
  //   // }
  //   return false;
  // }

  // /************************************
  //  * Next Step
  //  * V.1
  //  ***********************************/
  // nextStepClicked() {
  //   // this.nextStep.emit("tla");

  //   //TODO check if this step is completed.

  //   this.nextStep.emit();
  // }

  /************************************
   * Clear All variables
   * V.1
   ***********************************/
  clearAll() {
    if (this.dialog) this.dialog.show = false;
    this.loading.isLoading = false;
    this.shouldShowForm = false;
    this.dimmerService.dimWindow(false);
    this.shouldShowSavingStatus = false;
    this.dialogUpdating = false;
    this.formProcessing = false;
    this.isWeightNumerical = true;
  }

  // handleActionErrors() {
  //   if (this.actionErrors.length > 0) {
  //     //show alert - warning
  //     this.alertService.showAlert(
  //       false,
  //       "Error while updating the database.",
  //       false,
  //       this.actionErrors
  //     );
  //   }
  // }
}
